
import { useState, useRef, useEffect } from 'react';
import { useBlobImage } from "../service/Hooks/useBlobImage";

// Mini logo circulaire pour le dropdown
const DomainAvatar = ({ blobName, domainName, size = 32 }) => {
  const { imageUrl } = useBlobImage(blobName, "domains");

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={domainName}
        style={{
          width: size,
          height: size,
          minWidth: size,
          borderRadius: '50%',
          objectFit: 'contain',
          background: '#f8f9fa',
          border: '1px solid #e9ecef',
        }}
      />
    );
  }

  // Fallback initiale
  return (
    <div style={{
      width: size,
      height: size,
      minWidth: size,
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #4f8ef7, #1a56db)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: size * 0.38,
      fontWeight: 700,
      color: '#fff',
      flexShrink: 0,
    }}>
      {domainName?.charAt(0)?.toUpperCase() ?? 'D'}
    </div>
  );
};

export const DomainSelector = ({ domains, selectedDomain, onDomainChange, getRoleDisplay }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Fermer si on clique dehors
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!domains || domains.length <= 1) return null;

  return (
    <li className="nav-item" ref={ref} style={{ position: 'relative' }}>
      {/* Bouton déclencheur */}
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '6px 12px 6px 8px',
          background: open ? '#f0f5ff' : '#fff',
          border: '1.5px solid',
          borderColor: open ? '#4f8ef7' : '#dee2e6',
          borderRadius: '10px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: open ? '0 0 0 3px rgba(79,142,247,0.15)' : '0 1px 3px rgba(0,0,0,0.06)',
          minWidth: '180px',
          maxWidth: '220px',
          marginRight: '12px',
        }}
        onMouseEnter={(e) => {
          if (!open) {
            e.currentTarget.style.borderColor = '#4f8ef7';
            e.currentTarget.style.background = '#f8fbff';
          }
        }}
        onMouseLeave={(e) => {
          if (!open) {
            e.currentTarget.style.borderColor = '#dee2e6';
            e.currentTarget.style.background = '#fff';
          }
        }}
      >
        {selectedDomain && (
          <DomainAvatar
            blobName={selectedDomain.photo}
            domainName={selectedDomain.name}
            size={30}
          />
        )}
        <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
          <div style={{
            fontSize: '13px',
            fontWeight: 600,
            color: '#1a1a2e',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {selectedDomain?.name ?? 'Sélectionner'}
          </div>
          {selectedDomain && (
            <div style={{
              fontSize: '11px',
              color: '#6c757d',
              textTransform: 'capitalize',
            }}>
              {getRoleDisplay(selectedDomain)}
            </div>
          )}
        </div>
        {/* Chevron */}
        <svg
          width="14" height="14" viewBox="0 0 24 24" fill="none"
          style={{
            flexShrink: 0,
            transition: 'transform 0.2s ease',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            color: '#6c757d',
          }}
        >
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          right: 0,
          minWidth: '240px',
          background: '#fff',
          border: '1.5px solid #e9ecef',
          borderRadius: '12px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          zIndex: 1050,
          overflow: 'hidden',
          animation: 'domainDropdownIn 0.15s ease',
        }}>
          <style>{`
            @keyframes domainDropdownIn {
              from { opacity: 0; transform: translateY(-6px); }
              to   { opacity: 1; transform: translateY(0); }
            }
          `}</style>

          <div style={{ padding: '8px 12px 6px', borderBottom: '1px solid #f0f0f0' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Vos domaines
            </span>
          </div>

          {domains.map((domain) => {
            const isSelected = selectedDomain?.id === domain.id;
            return (
              <button
                key={domain.id}
                onClick={() => {
                  setOpen(false);
                  // Simuler l'event attendu par handleDomainChange
                  onDomainChange({ target: { value: domain.id } });
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                  padding: '10px 14px',
                  background: isSelected ? '#f0f5ff' : 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background 0.15s',
                  textAlign: 'left',
                  borderLeft: isSelected ? '3px solid #4f8ef7' : '3px solid transparent',
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.background = '#f8f9fa';
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.background = 'transparent';
                }}
              >
                <DomainAvatar blobName={domain.photo} domainName={domain.name} size={36} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: isSelected ? 700 : 500,
                    color: isSelected ? '#1a56db' : '#1a1a2e',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {domain.name}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'capitalize' }}>
                    {getRoleDisplay(domain)}
                  </div>
                </div>
                {isSelected && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6L9 17l-5-5" stroke="#4f8ef7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </li>
  );
};

export default DomainSelector;